import { useState, useEffect } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { images } from "../constants/images";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";
import { getBlogDetail, getBlogs } from "../api/utils";
import useFetch from "../api/hooks/useFetch";
import BlogDetailStyles from "./styles/BlogDetail";

export default function BlogDetail() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const id = location?.state?.item;
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  const [relatedType, setRelatedType] = useState("");

  // Fetch blog data from API
  const {
    apiData: blogData,
    isLoading,
    error,
  } = useFetch(() => getBlogDetail(slug), slug);

  // const {
  //   apiData: allBlogs,
  //   isLoading: isLoadingBlogs,
  //   error: blogsError,
  // } = useFetch(getBlogs, []);

  const {
    apiData: allBlogs,
    isLoading: isLoadingBlogs,
    error: blogsError,
  } = useFetch(
    () => getBlogs(relatedType),
    [relatedType], // dependency array
    false // don't auto-fetch initially
  );

  useEffect(() => {
    if (blogData?.categories) {
      setRelatedType(blogData.categories);
    }
  }, [blogData]);

  // Parse HTML content safely
  const parseHTMLContent = (htmlContent) => {
    if (!htmlContent) return "";

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    // Add IDs to headings for table of contents
    const headings = tempDiv.querySelectorAll("h2, h3");
    let tocItems = [];

    headings.forEach((heading, index) => {
      const id = `section-${index}`;
      heading.id = id;

      // Add to table of contents
      if (heading.tagName === "H2") {
        tocItems.push({
          id: id,
          title: heading.textContent.trim(),
        });
      }
    });

    return {
      content: tempDiv.innerHTML,
      tableOfContents: tocItems,
    };
  };

  // Process blog data
  const processBlogData = () => {
    if (!blogData) return null;

    const parsedContent = parseHTMLContent(blogData.content);

    return {
      id: blogData.id,
      title: blogData.title,
      slug: blogData.slug,
      excerpt: blogData.meta_description || "",
      category: blogData.categories || "ERP Development",
      author: {
        name: blogData.author || "Nishant Goyal",
        role: "Founder & CEO",
        bio: "Building custom business software since 2015. Helping companies escape the limitations of generic tools.",
        avatar: images?.founderPhoto || images?.aboutUs,
      },
      publishDate: blogData.posted_at
        ? new Date(blogData.posted_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "December 15, 2024",
      lastUpdated: blogData.posted_at || "December 15, 2024",
      readTime: calculateReadTime(blogData.content || ""),
      featuredImage: blogData.banner || images?.blog1 || images?.aboutUs,
      tags: blogData.categories
        ? [blogData.categories]
        : ["Custom Software", "Business Strategy"],
      tableOfContents: parsedContent.tableOfContents || [],
      content: parsedContent.content || "",
      metaDescription: blogData.meta_description || "",
      metaTitle: blogData.meta_title || "",
      isFeatured: blogData.featured === "Yes",
      leadMagnet: blogData.lead_magnet,
      ctaText: blogData.cta_text,
      ctaButtonText: blogData.cta_button_text,
    };
  };

  // Calculate read time
  const calculateReadTime = (content) => {
    if (!content) return "5 min read";
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, "");
    const wordCount = text.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const getRelatedPosts = () => {
    if (!allBlogs || !blogData) return [];

    // Filter blogs with the same category, excluding current blog
    const filteredBlogs = Array.isArray(allBlogs)
      ? allBlogs.filter(
          (blog) =>
            blog &&
            blog.slug &&
            blog.slug !== blogData.slug &&
            blog.id !== blogData.id &&
            blog.categories?.toLowerCase() ===
              blogData.categories?.toLowerCase()
        )
      : [];

    // Return only 3 random posts
    if (filteredBlogs.length > 3) {
      const shuffled = [...filteredBlogs].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, 3).map((blog) => ({
        id: blog.id,
        title: blog.title,
        slug: blog.slug,
        category: blog.categories || blogData.categories || "Custom Software",
        readTime: calculateReadTime(blog.content || ""),
        image: blog.banner || images?.aboutUs || "/default-blog.jpg",
      }));
    }

    // Return all filtered blogs if less than or equal to 3
    return filteredBlogs.map((blog) => ({
      id: blog.id,
      title: blog.title,
      slug: blog.slug,
      category: blog.categories || blogData.categories || "Custom Software",
      readTime: calculateReadTime(blog.content || ""),
      image: blog.banner || images?.aboutUs || "/default-blog.jpg",
    }));
  };

  // const getRelatedPosts = () => {
  //   if (!allBlogs || !blogData) return [];

  //   // Filter out current blog and get random 3 posts
  //   const filteredBlogs = Array.isArray(allBlogs)
  //     ? allBlogs.filter(
  //         (blog) =>
  //           blog &&
  //           blog.slug &&
  //           blog.slug !== blogData.slug &&
  //           blog.id !== blogData.id &&
  //           blog.category === blogData.category
  //       )
  //     : [];

  //   // Shuffle array and get first 3
  //   const shuffled = [...filteredBlogs].sort(() => 0.5 - Math.random());
  //   return shuffled.slice(0, 3).map((blog) => ({
  //     id: blog.id,
  //     title: blog.title,
  //     slug: blog.slug,
  //     category: blog.categories || "Custom Software",
  //     readTime: calculateReadTime(blog.content || ""),
  //     image: blog.banner || images?.aboutUs || "/default-blog.jpg",
  //   }));
  // };

  const blogPost = processBlogData();
  const relatedPosts = getRelatedPosts();
  const hasRelatedPosts = relatedPosts.length > 0;

  // Set page title and meta tags
  useEffect(() => {
    if (blogPost) {
      document.title = `${blogPost.title} | Plural Code Blog`;

      // Set meta description
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription && blogPost.metaDescription) {
        metaDescription.setAttribute("content", blogPost.metaDescription);
      }

      // Set Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute("content", blogPost.title);
      }

      const ogDescription = document.querySelector(
        'meta[property="og:description"]'
      );
      if (ogDescription && blogPost.metaDescription) {
        ogDescription.setAttribute("content", blogPost.metaDescription);
      }

      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage && blogPost.featuredImage) {
        ogImage.setAttribute("content", blogPost.featuredImage);
      }
    }

    window.scrollTo(0, 0);
  }, [blogPost, slug]);

  // Scroll to section
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth",
      });
      setShowTableOfContents(false);
    }
  };

  // Mobile Table of Contents Toggle
  const toggleTableOfContents = () => {
    setShowTableOfContents(!showTableOfContents);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="blog-detail-page loading">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading article...</p>
          </div>
        </div>
        <style jsx>{`
          .loading-spinner {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            padding: 40px;
          }
          .spinner {
            width: 50px;
            height: 50px;
            border: 3px solid #f0f0f0;
            border-top: 3px solid #0a2458;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
          }
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  // Error state
  if (error || !blogPost) {
    return (
      <div className="blog-detail-page error">
        <div className="container">
          <div className="error-message">
            <h2>Article Not Found</h2>
            <p>
              The blog post you're looking for doesn't exist or failed to load.
            </p>
            <a href="/blogs" className="btn btn-primary">
              Back to Blog
            </a>
          </div>
        </div>
        <style jsx>{`
          .error-message {
            text-align: center;
            padding: 80px 20px;
          }
          .error-message h2 {
            color: #dc3545;
            margin-bottom: 20px;
          }
          .error-message p {
            color: #666;
            margin-bottom: 30px;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <div className="blog-hero-content">
            {/* Breadcrumb */}
            <nav className="blog-breadcrumb">
              <a href="/" className="breadcrumb-link">
                Home
              </a>
              <span className="breadcrumb-separator">/</span>
              <a href="/blogs" className="breadcrumb-link">
                Blogs
              </a>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-current">{blogPost.title}</span>
            </nav>

            {/* Category Badge */}
            <div className="hero-badge">
              <span className="badge-icon">📄</span>
              <span className="badge-text">
                {blogPost.category === "ERP Development"
                  ? "Custom Business Software Development"
                  : blogPost.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="hero-title">{blogPost.title}</h1>

            {/* Excerpt */}
            <p className="hero-excerpt">{blogPost.excerpt}</p>

            {/* Meta Info */}
            <div className="hero-meta">
              <div className="author-info">
                <img
                  src={blogPost.author.avatar}
                  alt={blogPost.author.name}
                  className="author-avatar"
                />
                <div className="author-details">
                  <span className="author-name">{blogPost.author.name}</span>
                  <span className="author-role">{blogPost.author.role}</span>
                </div>
              </div>

              <div className="meta-divider"></div>

              <div className="meta-details">
                <div className="meta-item">
                  <span className="meta-icon">📅</span>
                  <span className="meta-text">{blogPost.publishDate}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-icon">⏱️</span>
                  <span className="meta-text">{blogPost.readTime}</span>
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="hero-share">
              <span className="share-label">Share this article:</span>
              <div className="share-buttons">
                <button
                  className="share-btn twitter"
                  onClick={() =>
                    window.open(
                      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                        blogPost.title
                      )}&url=${window.location.href}`,
                      "_blank"
                    )
                  }
                >
                  <span className="share-icon">𝕏</span>
                  <span className="share-text">Twitter</span>
                </button>
                <button
                  className="share-btn linkedin"
                  onClick={() =>
                    window.open(
                      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                        window.location.href
                      )}`,
                      "_blank"
                    )
                  }
                >
                  <span className="share-icon">💼</span>
                  <span className="share-text">LinkedIn</span>
                </button>
                <button
                  className="share-btn whatsapp"
                  onClick={() =>
                    window.open(
                      `https://wa.me/?text=${encodeURIComponent(
                        blogPost.title + " " + window.location.href
                      )}`,
                      "_blank"
                    )
                  }
                >
                  <span className="share-icon">💬</span>
                  <span className="share-text">WhatsApp</span>
                </button>
                <button
                  className="share-btn copy"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert("Link copied to clipboard!");
                  }}
                >
                  <span className="share-icon">📋</span>
                  <span className="share-text">Copy Link</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Background Elements */}
        <div className="hero-bg-elements">
          <div className="bg-circle-1"></div>
          <div className="bg-circle-2"></div>
          <div className="bg-circle-3"></div>
        </div>
      </section>

      {/* Featured Image */}
      {blogPost.featuredImage && (
        <section className="featured-image-section">
          <div className="container">
            <div className="featured-image-wrapper">
              <img
                src={blogPost.featuredImage}
                alt={blogPost.title}
                className="featured-image"
                onError={(e) => {
                  e.target.src = images?.aboutUs || "/default-blog.jpg";
                }}
              />
              <div className="image-caption">
                <span className="caption-icon">📸</span>
                <span className="caption-text">{blogPost.title}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="main-content-section">
        <div className="container">
          <div className="content-wrapper">
            {/* Mobile Table of Contents Toggle */}
            {/* {blogPost.tableOfContents.length > 0 && (
              <>
                <button
                  className="toc-toggle-btn"
                  onClick={toggleTableOfContents}
                >
                  <span className="toc-icon">📑</span>
                  <span className="toc-text">Table of Contents</span>
                  <span className="toc-arrow">
                    {showTableOfContents ? "↑" : "↓"}
                  </span>
                </button>

                {showTableOfContents && (
                  <div className="mobile-toc">
                    <h3 className="toc-title">Contents</h3>
                    <ul className="toc-list">
                      {blogPost.tableOfContents.map((item, index) => (
                        <li key={index} className="toc-item">
                          <button
                            onClick={() => scrollToSection(item.id)}
                            className="toc-link"
                          >
                            <span className="toc-number">0{index + 1}</span>
                            <span className="toc-title-text">{item.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )} */}

            <div className="content">
              <div></div>

              {/* Article Content */}
              <div className="article-content">
                {blogPost.content ? (
                  <div
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: blogPost.content }}
                  />
                ) : (
                  <div className="no-content">
                    <p>Content not available for this article.</p>
                  </div>
                )}

                {/* Tags */}
                <div className="article-tags">
                  <h3 className="tags-title">Article Tags</h3>
                  <div className="tags-list">
                    {blogPost.tags.map((tag, index) => (
                      <a
                        key={index}
                        href={`/blog/tag/${tag
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        className="tag-item"
                      >
                        #{tag}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Author Bio */}
                <div className="author-bio-card">
                  <div className="author-header">
                    <img
                      src={blogPost.author.avatar}
                      alt={blogPost.author.name}
                      className="author-bio-avatar"
                    />
                    <div className="author-info">
                      <h3 className="author-name">{blogPost.author.name}</h3>
                      <p className="author-role">{blogPost.author.role}</p>
                    </div>
                  </div>
                  <p className="author-bio">{blogPost.author.bio}</p>
                  <div className="author-social">
                    <a href="/contact" className="social-link">
                      Contact →
                    </a>
                  </div>
                </div>

                {/* CTA Section from API */}
                {/* {blogPost.leadMagnet && (
                  <div className="lead-magnet">
                    <h3>Download Free Resource</h3>
                    <p>{blogPost.leadMagnet.title}</p>
                    <a
                      href={`/lead-magnet/${blogPost.leadMagnet.slug}`}
                      className="btn btn-primary"
                    >
                      Download Now →
                    </a>
                  </div>
                )} */}

                {/* Share Again */}
                <div className="share-again">
                  <h3 className="share-title">Found this article helpful?</h3>
                  <p className="share-subtitle">
                    Share it with other business leaders who might benefit
                  </p>
                  <div className="share-buttons-large">
                    <button
                      className="share-btn-lg twitter"
                      onClick={() =>
                        window.open(
                          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                            blogPost.title
                          )}&url=${window.location.href}`,
                          "_blank"
                        )
                      }
                    >
                      <span className="share-icon">𝕏</span>
                      <span>Share on Twitter</span>
                    </button>
                    <button
                      className="share-btn-lg linkedin"
                      onClick={() =>
                        window.open(
                          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                            window.location.href
                          )}`,
                          "_blank"
                        )
                      }
                    >
                      <span className="share-icon">💼</span>
                      <span>Share on LinkedIn</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {/* <section className="related-posts-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Related Articles</h2>
            <p className="section-subtitle">
              Continue reading more about custom software development
            </p>
          </div>

          <div className="related-posts-grid">
            {relatedPosts.map((post) => (
              <a
                href={`/blog/${post.slug}`}
                key={post.id}
                className="related-post-card"
              >
                <div className="post-image-wrapper">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="post-image"
                  />
                  <span className="post-category">{post.category}</span>
                </div>
                <div className="post-content">
                  <h3 className="post-title">{post.title}</h3>
                  <div className="post-meta">
                    <span className="post-read-time">{post.readTime}</span>
                  </div>
                </div>
                <div className="post-arrow">→</div>
              </a>
            ))}
          </div>

          <div className="cta-section">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Escape Generic Software?</h2>
              <p className="cta-text">
                Book a free consultation to explore custom solutions for your business
              </p>
              <div className="cta-buttons">
                <a href="https://calendly.com/pluralcode2016/quick-chat" className="cta-btn-primary">
                  Schedule Free Consultation →
                </a>
                <a href="/blogs" className="cta-btn-secondary">
                  Browse All Articles →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <section className="related-posts-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Related Articles</h2>
            <p className="section-subtitle">
              Continue reading more about custom software development
            </p>
          </div>

          {/* Loading state for related posts */}
          {isLoadingBlogs ? (
            <div className="loading-related">
              <div className="loading-spinner-small"></div>
              <p>Loading related articles...</p>
            </div>
          ) : blogsError ? (
            <div className="error-related">
              <p>Unable to load related articles.</p>
            </div>
          ) : hasRelatedPosts ? (
            <div className="related-posts-grid">
              {relatedPosts.map((post) => (
                <Link
                  to={`/blogs/${post.slug}`}
                  key={post.id}
                  className="related-post-card"
                >
                  <div className="post-image-wrapper">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="post-image"
                      onError={(e) => {
                        e.target.src = images?.aboutUs || "/default-blog.jpg";
                      }}
                    />
                    <span className="post-category">{post.category}</span>
                  </div>
                  <div className="post-content">
                    <h3 className="post-title">{post.title}</h3>
                    <div className="post-meta">
                      <span className="post-read-time">{post.readTime}</span>
                    </div>
                  </div>
                  <div className="post-arrow">
                    Read <span>→</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="no-related-posts">
              <div className="no-related-content">
                <h3 className="no-related-title text-center">
                  No matching category found
                </h3>
                <p className="no-related-message text-center">
                  We couldn't find other articles in the{" "}
                  <strong>{blogPost.category}</strong> category.
                </p>
              </div>
            </div>
          )}

          <div className="cta-section">
            <div className="cta-content">
              <h2 className="cta-title">Ready to Escape Generic Software?</h2>
              <p className="cta-text">
                Book a free consultation to explore custom solutions for your
                business
              </p>
              <div className="cta-buttons">
                <Link
                  to="https://topmate.io/pluralcode/1677497"
                  className="cta-btn-primary"
                >
                  Schedule Free Consultation →
                </Link>
                <Link to="/blogs" className="cta-btn-secondary">
                  Browse All Articles →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <BlogDetailStyles />
    </div>
  );
}
